import {
  useDeviceSize,
  Breakpoint,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import {
  Square,
  Image,
  Flex,
  Stack,
  Section,
} from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import React, { useState, useEffect } from 'react';
import { Transition } from 'react-transition-group';
import { Helmet } from 'react-helmet-async';
import { getWindow } from 'src/utilities/window';
import { SignupProgressTracker } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTracker';
import {
  getSignupSteps,
  STEP_CONTENT,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { SignOutButton } from 'src/components/common/features/Signup/components/SignOutButton/SignOutButton';
import SignupNavBar from 'src/components/Navigation/SignupNavBar';
import { getConfig } from 'src/utilities/config';
import { FIXED_SIGNUP_FOOTER_ID } from 'src/components/common/features/Signup/constants';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import { capitalize } from 'src/utilities/string-utils';

const illustrationUrl =
  'https://media-cdn.grubhub.com/grubhub-assets/image/upload/v1773759077/corp-management-ui/balance/Group%20Order%20Illustration_png.png';

const wizardStepEnterDuration = 500;
const wizardStepExitDuration = 1000;
const defaultWizardStepStyles = {
  transition: `all ${wizardStepEnterDuration}ms ease-in`,
  opacity: 0,
};
const wizardStepTransitionStyles = {
  entering: {
    opacity: 1,
  },
  entered: {
    opacity: 1,
  },
  exiting: {
    opacity: 0,
  },
  exited: {
    opacity: 0,
  },
};

const FIXED_FOOTER_HEIGHT = '76px';

export function SignupWizard(): JSX.Element {
  const { breakpoint } = useDeviceSize(getWindow());
  const { activeStep, currentStepNumber } = useSignupStatus();
  const [isMounted, setIsMounted] = useState(false);
  const [currentActiveStep, setCurrentActiveStep] = useState(activeStep);
  const { MEAL_CREDIT_ENTITY_NAME: entityName } = useMealCreditEntityName();
  const [wizardSignupSteps] = useState(
    getSignupSteps(false, capitalize(entityName.singular))
  );
  const {
    site: { standardTitle: siteTitle },
  } = getConfig();

  const isTablet = breakpoint >= Breakpoint.sm;
  const isPhone = !isTablet;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (currentActiveStep !== activeStep) {
      // give the exit animation time to finish before updating
      setTimeout(() => {
        setCurrentActiveStep(activeStep);
      }, wizardStepEnterDuration);
    }
  }, [activeStep, currentActiveStep]);

  return (
    <section
      className={classnames('signupWizard', {
        twoColumns: isTablet,
      })}
    >
      <Helmet defer={false}>
        <title>{`${siteTitle} | Signup - Step ${currentStepNumber}`}</title>
      </Helmet>
      <Flex
        flexDirection="column"
        backgroundColor={isPhone ? 'globals.white' : 'globals.stone10'}
        className="signupWizard_signupLeftSidebar"
      >
        <Stack y={isTablet ? 'lg' : 'none'}>
          <SignupNavBar />
        </Stack>
        <Flex
          flexGrow={isPhone ? 0 : 1}
          flexDirection="column"
          justifyContent="space-between"
          className={classnames('', {
            signupWizard_hasFixedHeader: isPhone,
          })}
        >
          <Square
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(!isTablet && { inset: 'md' })}
            overflowY={isPhone ? 'auto' : 'initial'}
            height="100%"
            margin={isTablet ? '0 48px' : '0'}
          >
            <SignupProgressTracker steps={wizardSignupSteps} />
          </Square>
          {isPhone && (
            <Square
              flexBasis="auto"
              flexGrow={0}
              flexShrink={0}
              maxHeight={FIXED_FOOTER_HEIGHT}
              id={FIXED_SIGNUP_FOOTER_ID}
            />
          )}
          {isTablet && (
            <Square
              width="100%"
              inset="lg"
              className="signupWizard_illustrationContainer"
            >
              <Image
                src={illustrationUrl}
                width={260}
                alt=""
                role="presentation"
              />
            </Square>
          )}
        </Flex>
      </Flex>
      <Flex
        flexDirection="column"
        height="100vh"
        className="signupWizard_signupMainContentContainer"
      >
        <Flex
          alignItems="flex-start"
          justifyContent="center"
          flexGrow={1}
          className={classnames('signupWizard_signupMainContent', {
            mounted: isMounted,
          })}
        >
          {isTablet && (
            <div className="signupWizard_headerGradient">
              <Square inset="md">
                <SignOutButton />
              </Square>
            </div>
          )}
          {isTablet && (
            <Transition
              in={currentActiveStep === activeStep}
              timeout={{
                enter: wizardStepEnterDuration,
                exit: wizardStepExitDuration,
              }}
              mountOnEnter
              unmountOnExit
            >
              {(transitionState) => (
                <Section
                  inset="md"
                  margin="auto"
                  width="100%"
                  style={{
                    ...defaultWizardStepStyles,
                    ...wizardStepTransitionStyles[transitionState],
                  }}
                >
                  {STEP_CONTENT[currentActiveStep]}
                </Section>
              )}
            </Transition>
          )}
        </Flex>
        {isTablet && (
          <Square
            flexBasis="auto"
            flexGrow={0}
            flexShrink={0}
            id={FIXED_SIGNUP_FOOTER_ID}
          />
        )}
      </Flex>
    </section>
  );
}
